export interface VelodromeAxis {
  major_axis_bearing_deg: number;
  minor_axis_bearing_deg: number;
  confidence: number;
  n_samples_used: number;
}

export interface LapMarkers {
  edited?: boolean | null;
  idx_end?: number[] | null;
  idx_start?: number[] | null;
  inactive_laps: boolean[] | null;
  markers_rel_ts?: number[] | null;
  start_ts?: number | null;
  velodrome_axis?: VelodromeAxis | null;
}

// Health status enum values (mirrors backend RunHealth IntEnum)
export enum RunHealthStatus {
  GOOD = 0,
  NONE = 1,
  AVERAGE = 50,
  WARNING = 100,
  ERROR = 500,
}

export enum ProcessingStage {
  INGESTION = 1500,
  METADATA = 2000,
  SENSORS = 3000,
  SESSION_SPECIFIC_PRE_GRID = 4000,
  ROUTE_PRE_GRID = 5000,
  HEALTH_CHECK = 5500,
  GRID = 6000,
  ROUTE_POST_GRID = 6500,
  SESSION_SPECIFIC_POST_GRID = 7000,
  CDA_CALCULATION = 8000,
  POST_CDA_CALCULATION = 9000,
  FINALIZATION = 10000,
  COMPLETE = 100000,
}

export function getStageName(code: number | null | undefined): string {
  if (code == null) return 'Unknown';
  if (code >= 100000) return 'Complete';
  if (code >= 10000) return 'Finalization';
  if (code >= 9000) return 'Post-CdA';
  if (code >= 8000) return 'CdA Calculation';
  if (code >= 7000) return 'Post-Grid Processing';
  if (code >= 6500) return 'Route Post-Grid';
  if (code >= 6000) return 'Grid';
  if (code >= 5500) return 'Health Check';
  if (code >= 5000) return 'Route Pre-Grid';
  if (code >= 4000) return 'Pre-Grid Processing';
  if (code >= 3000) return 'Sensors';
  if (code >= 2000) return 'Metadata';
  if (code >= 1500) return 'Ingestion';
  return 'Not Started';
}

// Timeseries summary used as both raw (L00) and gridded (G10/G15) views.
// Timing fields stay null on the gridded view.
export interface SensorChannelStats {
  n_samples: number;
  duration_s: number | null;
  sample_rate_hz: number | null;
  dt_std_s: number | null;
  min: Record<string, number>;
  max: Record<string, number>;
  mean: Record<string, number>;
}

// Per-sensor presence + raw/gridded value distribution. Subclassed in
// the server for sensors carrying channel-quality extras (GPS accuracy,
// ANT packet loss).
export interface SensorHealthBase {
  present: boolean;
  raw: SensorChannelStats;
  gridded: SensorChannelStats;
  // Derived server-side: true iff the sensor logged samples on the raw OR
  // gridded view. Gates the "Good"/"Missing" badge instead of the looser
  // `present` flag (which only records the channel/file existed). Optional —
  // older payloads omit it, so callers fall back to the sample counts.
  has_data?: boolean;
}

export interface AntSensorHealth extends SensorHealthBase {
  sensor_key: string;
  packet_count_sequential: boolean;
  packets_dropped: number;
  packets_dropped_fraction: number | null;
  packets_collision: number;
  // cadence_source moved to solve.selections.cadence_source
}

export interface GpsHealth extends SensorHealthBase {
  accuracy_p90: number | null;
  pct_under_4m: number | null;
  pct_4_to_8m: number | null;
  pct_8_to_20m: number | null;
  pct_over_20m: number | null;
}

// IMU (accel/gyro/mag) — bare base; signal-rate stability is captured
// by raw.dt_std_s, no extra fields needed.
export type ImuSensorHealth = SensorHealthBase;

export interface GateHealth {
  present: boolean;
  gate_a_lat: number | null;
  gate_a_lon: number | null;
  gate_a_beta: number | null;
  gate_b_lat: number | null;
  gate_b_lon: number | null;
  gate_b_beta: number | null;
  gates_lat: number[] | null;
  gates_lon: number[] | null;
  bearing_deg: number | null;
  route_and_gates_intersect?: boolean | null;
  gates_defined_count?: number;
  gates_crossed_count?: number;
}

export interface RouteHealth {
  present: boolean;
  start_lat: number | null;
  start_lon: number | null;
  end_lat: number | null;
  end_lon: number | null;
  route_length_m: number | null;
  segment_count: number | null;
  bearing_deg: number | null;
}

export interface GateIntersection {
  route_and_gates_intersect: boolean | null;
  gates_defined_count: number;
  gates_crossed_count: number;
  // fallback_level moved to solve.selections.route_fallback_level
}

// --- Solve object types ---

export type GateStatus = 'UNUSED' | 'START' | 'SPLIT' | 'FINISH';

export interface SolveGate {
  lat: number;
  lon: number;
  pos?: number | null;
  bearing?: number | null;
  status: GateStatus;
}

export interface SolveRouteMetadata {
  route_origin: string;
  route_origin_init: string;
  gates_origin: string;
  gates_origin_init: string;
  route_valid: boolean;
  gates_valid: boolean;
  incorrect_db_data: boolean;
  route_type: string;
  route_category: number;
  fallback_used: boolean;
  fallback_detail?: string | null;
}

// Static inputs to the solver — snapshot of CalibrationConst plus DB-
// lookup route/gate identity. Replaces the legacy flat fields that
// used to sit directly on SolveData. Old v1 records are auto-lifted
// into this shape by the Python read-side validator.
export interface SolveInputs {
  combined_mass?: number | null;
  crr?: number | null;
  cp_corr?: number | null;
  yaw_offset?: number | null;
  wheel_circumference?: number | null;
  input_wheel_circumference?: number | null;
  power_meter_slope?: number | null;
  alt_power?: number | null;
  alt_spd?: number | null;
  gps_speed?: boolean | null;
  head_slope?: number | null;
  head_ref?: number | null;
  cda_ref?: number | null;
  airspeed_ref?: number | null;
  calculation_version?: number | null;
  session_analysis_type?: number | null;
  front_tooth_count?: number | null;
  gate_id?: string | null;
  route_id?: string | null;
  gate_hash?: string | number | null;
  gates?: SolveGate[] | null;
  route_metadata?: SolveRouteMetadata | null;
}

export interface SpeedSelection {
  instance: number | null;
  page_family: string | null;
  source: string | null;
  gps_primary: boolean;
  gps_fallback: boolean;
}

export interface PowerMeterSelection {
  instance: number | null;
  page: number | null;
  source_label: string | null;
}

// Runtime branch decisions the solver made — what was chosen at each
// fallback point. Every field here describes a choice with at least
// one alternative; static inputs go in SolveInputs instead.
export interface SolveSelections {
  speed: SpeedSelection;
  power_meter: PowerMeterSelection;
  cadence_source: string | null;
  velodrome_bend_signal: string | null;
  route_fallback_level: number | null;
  weather_sources: WeatherSourceUsage[];
}

export interface SolveData {
  version: number;
  inputs: SolveInputs;
  selections: SolveSelections;
}

export interface BodySensorHealth extends AntSensorHealth {
  ref_chest_cm: number | null;
  ref_head_cm: number | null;
  ref_waist_cm: number | null;
}

export interface WindDiffRefs {
  diff_ref_1_pa: number | null;
  diff_ref_2_pa: number | null;
  diff_ref_3_pa: number | null;
}


export interface SensorInfoObj {
  ant_device_number: number | null;
  ant_id_not_found_reason: string | null;
  battery_voltage: number | null;
  firmware_version: string | null;
}

export interface SpeedHealthObj {
  wheel_skip_count: number | null;
  wheel_skip_ratio: number | null;
  gps_wheel_crc_in_tolerance: number;
  tolerance: number;
}

export enum DiffSensorStatus {
  GOOD = 0,
  NEEDS_AUTOCAL = 1,
  ERROR = 2,
  UNSTABLE = 3,
  UNKNOWN = 4,
}

export interface DiffChannelHealth {
  start_median_pa: number | null;
  start_std_pa: number | null;
  end_median_pa: number | null;
  end_std_pa: number | null;
  reference_pa: number | null;
  status: number;
}

export interface ProbeHealth {
  mean_abs_dev: number | null;
  n_samples: number | null;
  status: number;
}

export interface WindSensorHealth {
  diff1: DiffChannelHealth;
  diff2: DiffChannelHealth;
  diff3: DiffChannelHealth;
  probe_health?: ProbeHealth;
}

// ── Lap parsing health diagnostics ──

export interface CatesbyLapDetection {
  lap_number: number;
  threshold_crossing_idx: number;
  stability_confirmed_idx: number;
  lap_start_idx: number;
  lap_finish_idx: number;
  threshold_crossing_time_s: number;
  stability_confirmed_time_s: number;
  lap_start_time_s: number;
  lap_finish_time_s: number;
  lap_duration_s: number;
  threshold_crossing_distance_m: number;
  lap_start_distance_m: number;
  lap_finish_distance_m: number;
  deceleration_alert: boolean;
  active: boolean;
}

export interface CatesbyRejectedCrossing {
  crossing_idx: number;
  crossing_time_s: number;
  crossing_distance_m: number;
  target_start_idx?: number | null;
  target_start_distance_m?: number | null;
  reason: string;
}

export interface VelodromeLapDetection {
  lap_number: number;
  lap_start_idx: number;
  lap_finish_idx: number;
  lap_start_time_s: number;
  lap_finish_time_s: number;
  lap_duration_s: number;
  lap_start_distance_m: number;
  lap_finish_distance_m: number;
  active: boolean;
}

export interface LapParsingHealth {
  parser_type: string;
  laps_detected: number;
  laps_active: number;
  // Velodrome-specific fields (present when parser_type === 'velodrome')
  correction_applied?: boolean;
  correction_failed?: boolean;
  correction_error?: string | null;
  // Catesby-specific fields (present when parser_type === 'catesby')
  speed_threshold_revs?: number;
  stability_window_s?: number;
  distance_offset_m?: number;
  lap_length_m?: number;
  wheel_circumference_m?: number;
  data_interval_s?: number;
  speed_80th_percentile_revs?: number | null;
  total_crossings_found?: number;
  total_distance_m?: number | null;
  total_duration_s?: number | null;
  deceleration_warning?: boolean;
  laps_with_deceleration?: number;
  // Per-lap detection records; shape depends on parser_type. Narrow with
  // an `as CatesbyLapDetection[]` / `as VelodromeLapDetection[]` cast.
  laps?: (CatesbyLapDetection | VelodromeLapDetection)[];
  rejected_crossings?: CatesbyRejectedCrossing[];
}

export interface WhlRouteLapResult {
  n_valid: number;
  slope: number;
  whlcrc: number;
  kept: boolean;
  removed_by?: string | null; // 'length' | 'tolerance' | 'whlcrc'
}

export interface WhlRouteMappingHealth {
  cardinal_laps_found: number;
  initial_results: number;
  results: WhlRouteLapResult[];
  final_results: number;
  min_route_length_n: number;
  whlcrc_expected: number;
  whlcrc_deviation?: number | null;
  circumference_calculated?: number | null;
  l20_valid_fraction?: number | null;
}

export interface TraverseGateFinderHealth {
  gate_low: number;
  gate_high: number;
  n_grid_points: number;
  n_valid_points: number;
  n_nan_points: number;
  first_valid_idx?: number | null;
  traverses_detected: number;
  gate_crossings: number;
  direction_changes: number;
  aborted_traverses: number;
}

export interface CdaOutputHealth {
  cda_present: boolean;
  cda_normalised_present: boolean;
  n_traverses: number;
  n_cda_finite: number;
  n_cda_normalised_finite: number;
}

// Outcome of the crosswind/yaw-offset (Cp) bearing-split analysis. When the
// direction split collapses (one direction with zero laps within the cone),
// the yaw offset comes out non-finite; the run still produces a normalised
// CdA from the uncorrected yaw, but `valid` is false. Widen
// `parser_vars.traverse.cp_bearing_cone` to recover the correction.
export interface CpAnalysisHealth {
  attempted: boolean;
  valid: boolean;
  out_lap_count: number;
  in_lap_count: number;
  bearing_cone_deg?: number | null;
  cp_adj?: number | null;
  yaw_offset_deg?: number | null;
}

export interface RunHealthObj {
  sensors_present: {
    power_meter: AntSensorHealth;
    wind: AntSensorHealth;
    aero?: AntSensorHealth; // legacy key, use wind instead
    speed: AntSensorHealth;
    body?: BodySensorHealth;
    power_meter_2?: AntSensorHealth;
    power_meter_3?: AntSensorHealth;
    speed_2?: AntSensorHealth;
    speed_3?: AntSensorHealth;
    gps: GpsHealth;
    accel?: ImuSensorHealth;
    gyro?: ImuSensorHealth;
    mag?: ImuSensorHealth;
    gates: GateHealth;
    gates_run_original?: GateHealth;
    route: RouteHealth;
  };
  missing_sensors: string[];
  missing_optional_sensors?: string[];
  wind_diff_refs?: WindDiffRefs;
  sensor_info?: Record<string, SensorInfoObj>;
  gate_intersection?: GateIntersection;
  speed_health: SpeedHealthObj;
  wind_sensor_health?: WindSensorHealth;
  health_status: number;
  health_status_name: string;
  health_status_reasons?: string[];
  health_code?: number;
  total_run_time_seconds?: number;
  lap_parsing?: LapParsingHealth | null;
  weather?: WeatherHealth | null;
  whl_route_mapping?: WhlRouteMappingHealth | null;
  traverse_gate_finder?: TraverseGateFinderHealth | null;
  cda_output?: CdaOutputHealth | null;
  cp_analysis?: CpAnalysisHealth | null;
}

// ── Weather data health ──

export type WeatherStatus = 'good' | 'fallback' | 'failed';

export interface WeatherSourceUsage {
  channel: string;
  requested_source: string;
  actual_source: string | null;
  fallback_reason?: string | null;
  fallback_chain_tried?: string[];
}

export interface WeatherHealth {
  status: WeatherStatus;
  sources_used: WeatherSourceUsage[];
}

// ── Parser variable overrides ──

export interface CatesbyParserVars {
  speed_threshold?: number | null;
  distance_offset?: number | null;
  stability_window?: number | null;
  acceleration_smoothing_s?: number | null;
  lap_length?: number | null;
  min_duration?: number | null;
  return_distance_offset?: number | null;
  return_lap_length?: number | null;
}

export interface TraverseParserVars {
  min_valid_points?: number | null;
  max_radius?: number | null;
  combine_gaps?: number | null;
  min_distance?: number | null;
  // Wheel→route regression filter (map_whl_to_route)
  wheel_circumference?: number | null; // band centre; defaults to config wheel circ
  whlcrc_deviation?: number | null; // band half-width (m); default 0.3
  min_route_length_n?: number | null; // min revs per lap; default 15
  cp_bearing_cone?: number | null; // Cp direction-split cone (deg); default 2.5
}

export interface ParserVars {
  version: number;
  catesby?: CatesbyParserVars | null;
  velodrome?: Record<string, unknown> | null;
  traverse?: TraverseParserVars | null;
}

// ── Sensor offset overrides ──

export interface WindSensor5ZeroOverride {
  diff1_zero_pa?: number | null;
  diff2_zero_pa?: number | null;
  diff3_zero_pa?: number | null;
  override_ts?: number | null;
  source?: string | null;
}

export interface SensorOffsets {
  version: number;
  wind_sensor_5?: WindSensor5ZeroOverride | null;
}

export type SensorOffsetsScope = 'this' | 'after' | 'before' | 'all';

// ── Reprocess progress ──

/**
 * Shape returned by `GET /runs/<id>/progress` — a faithful 1:1 of the
 * lightweight backend endpoint (run_progress_controller.py). Used to drive
 * per-run reprocess indicators while a reprocess is in flight.
 *
 * Note: `processing_stage` is a free-text pipeline stage NAME (e.g. 'QUEUED',
 * 'STARTED', 'Grid'), not a numeric code — use `humanizeStage()` to display it.
 */
export interface RunProgress {
  id: string;
  run_status: number;
  processing_stage: string | null;
  processing_started_at: string | null;
  data_gridded_pending: boolean;
  edit_ts: string | null;
}

export interface Run {
  id: string;
  run_ts: string;
  run_status: number;
  description: string;
  hidden: boolean;
  deleted: boolean;
  run_number: number;
  timezone?: string | null;
  average: Record<string, number[]>;
  deltas: Record<string, number[]>;
  traverse: Record<string, number[]>;
  yaw_offset?: number;
  solve?: SolveData | null;
  parser_vars?: ParserVars | null;
  sensor_offsets?: SensorOffsets | null;
  lap_markers?: LapMarkers | null;
  run_health?: number | null;
  run_health_obj?: RunHealthObj | null;
  run_health_reasons?: string[] | null;
  health_code?: number | null;
  // Reprocess progress columns (present when the run query scope includes them)
  processing_stage?: string | null;
  processing_started_at?: string | null;
  data_gridded_pending?: boolean | null;
}
